'use client';

import { formatPrice } from '@lichtblick/localization';
import { BoxCTA } from '@lichtblick/photon/atoms/box-cta/box-cta';
import { Headline } from '@lichtblick/photon/atoms/headline/headline';
import { Text } from '@lichtblick/photon/atoms/text/text';
import { FlameS } from '@lichtblick/photon/icons/energy/flame-s';
import { Colors } from '@lichtblick/theme';
import { useTracker } from '@lichtblick/tracker';
import { useActionState, useState } from 'react';
import { getLoyaltyBonusInEur } from '@/components/card/card-utils';
import { GasTariffDetailsLayer } from '@/components/tariff-details-layer/gas-tariff-details-layer';
import { useLoggedIn } from '@/tracking/use-logged-in';
import { createEcommerceItem, getGasTariffIndex } from '../../tracking/create-ecommerce-item';
import type { TariffsQuery } from '../../types/generated';
import { DurationToggle } from '../duration-toggle/duration-toggle';
import { PriceBreakdownList } from '../price-breakdown/price-breakdown-list';
import { PriceBreakdownRow } from '../price-breakdown/price-breakdown-row';
import { useGasTariffs } from '../tariffs-providers/use-gas-tariffs';
import { Card, CardHeader, CardPrice } from './card';
import styles from './card.module.scss';

type CardProps = {
  cardIndex: number;
  initialActiveIndex: 0 | 1;
  isComboCheckout: boolean;
  products: [TariffsQuery['tariffs'][number], TariffsQuery['tariffs'][number]];
  selectAction: (state: undefined, formData: FormData) => Promise<undefined>;
  tariffsCount: number;
};

export const GasCard: React.FC<CardProps> = ({
  cardIndex,
  initialActiveIndex,
  isComboCheckout,
  products,
  selectAction,
  tariffsCount,
}) => {
  const tariffsData = useGasTariffs();

  const { trackEcommerceEvent } = useTracker();
  const loggedIn = useLoggedIn();

  const [active, setActive] = useState(initialActiveIndex);
  const [, dispatch, pending] = useActionState(selectAction, undefined);

  const loyaltyBonusInEur = getLoyaltyBonusInEur(products[active]);
  const instantBonusInEur = products[active].bonus?.instantBonusInEur;
  const hasBonus = Boolean(loyaltyBonusInEur) || Boolean(instantBonusInEur);

  return (
    <Card
      aria-label={`Tarif ${products[active].name}`}
      data-testid={`tariff-card-gas`}
      data-testproduct={products[active].productCode}
      role="listitem"
    >
      <CardHeader color="DarkAqua">
        <FlameS color={Colors.DarkAqua} />
        <Headline align="center" size="s">
          {products[active].name}
        </Headline>
        <Text align="center" bold className={styles.text} size="l">
          {products[active].subLine}
        </Text>
      </CardHeader>

      <CardPrice background="FadedAqua" color="DarkAqua">
        <Headline size="m">{formatPrice(products[active].monthlyPriceWithBonus)}</Headline>
        <Text bold size="xs">
          {hasBonus ? 'ø Kosten pro Monat' : 'Abschlag pro Monat'}
        </Text>
        <Text bold size="xs">
          {hasBonus ? <>{formatPrice(products[active].monthlyRate)} (ohne Bonus)</> : <br />}
        </Text>

        <DurationToggle
          activeIndex={active}
          firstOption={`${products[0].contractPeriod?.value} Monate`}
          onToggle={(index) => {
            setActive(index);

            if (index !== initialActiveIndex) {
              trackEcommerceEvent({
                event: 'Ecommerce - Item List Views',
                event_name: 'view_item_list',
                view_item_list: createEcommerceItem(
                  [
                    {
                      ...products[index],
                      itemIndex: getGasTariffIndex(cardIndex, index, tariffsCount),
                    },
                  ],
                  loggedIn,
                  isComboCheckout,
                ),
              });
            }
          }}
          secondOption={`${products[1].contractPeriod?.value} Monate`}
        />

        <Text size="xs">Energiepreisgarantie und Laufzeit²</Text>
      </CardPrice>

      <PriceBreakdownList>
        {Boolean(instantBonusInEur) && (
          <PriceBreakdownRow label="Sofortbonus nach 60 Tagen" value={formatPrice(instantBonusInEur)} />
        )}

        {Boolean(loyaltyBonusInEur) && (
          <PriceBreakdownRow label="Treuebonus nach 365 Tagen" value={formatPrice(loyaltyBonusInEur)} />
        )}

        <PriceBreakdownRow label="Arbeitspreis" value={formatPrice(products[active].workPrice, 'ct/kWh')} />
        <PriceBreakdownRow label="Monatl. Grundpreis" value={formatPrice(products[active].monthlyBasePrice)} />
      </PriceBreakdownList>

      <GasTariffDetailsLayer
        isComboCheckout={isComboCheckout}
        //                                         index --> 0, 1, 2, 3, 4, 5
        // tariffs are being tracked in the visual order -> 24,24,24,12,12,12
        index={active === 1 ? cardIndex + tariffsCount : cardIndex}
        product={products[active]}
      />

      <form
        action={dispatch}
        className={styles.form}
        onSubmit={() => {
          trackEcommerceEvent({
            event: 'Ecommerce - Add to cart',
            event_name: 'add_to_cart',
            add_to_cart: createEcommerceItem(
              [
                {
                  ...products[active],
                  itemIndex: getGasTariffIndex(cardIndex, active, tariffsData?.tariffs.length as number),
                },
              ],
              loggedIn,
              isComboCheckout,
            ),
          });
        }}
      >
        <input name="gasProductCode" type="hidden" value={products[active].productCode ?? ''} />
        <BoxCTA
          ariaLabel={`Tarif ${products[active].name} auswählen`}
          as="button"
          data-dd-action-name={`Select product: ${products[active].productCode}`}
          data-testid="button-select-product"
          loading={pending}
          wide
        >
          Den will ich
        </BoxCTA>
      </form>
    </Card>
  );
};
